import { Controller } from "@hotwired/stimulus"

export default class extends Controller {

  static targets = [
    "address",
    "city",
    "streetNumber",
    "route",
    "postalCode",
    "country",
    "county",
    "state",
    "longitude",
    "latitude",
  ]

  static values = {
    country: Array,
    apiKey: String
  }

  initialize() {
    this.placeChanged = this.placeChanged.bind(this)
  }

  connect() {
    this.loadGoogleMapsAPI().then(() => {
      this.initAutocomplete()
    }).catch(error => {
      console.error("Error loading Google Maps API:", error)
    })
  }
  
  loadGoogleMapsAPI() {

    if (typeof google !== "undefined" && google.maps && google.maps.places) {
      return Promise.resolve()
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement("script")
      const apiKey = this.apiKeyValue || ""
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`
      script.async = true
      script.defer = true
      
      script.onload = () => {
        resolve()
      }
      
      script.onerror = () => {
        reject(new Error("Failed to load Google Maps API"))
      }
      
      document.head.appendChild(script)
    })
  }
  
  initAutocomplete() {
    this.autocomplete = new google.maps.places.Autocomplete(this.addressTarget, this.autocompleteOptions)
    this.autocomplete.addListener("place_changed", this.placeChanged)
  }

  placeChanged() {
    this.place = this.autocomplete.getPlace()
    const addressComponents = this.place.address_components

    if (addressComponents !== undefined) {
      const formattedAddress = this.formatAddressComponents(addressComponents)

      this.setAddressComponents(formattedAddress)
    }

    if (this.place.geometry !== undefined) {
      this.setGeometry(this.place.geometry)
    }
  }

  setAddressComponents(address) {
    if (this.hasStreetNumberTarget) this.streetNumberTarget.value = address.street_number || ""
    if (this.hasRouteTarget) this.routeTarget.value = address.route || ""
    if (this.hasCityTarget) this.cityTarget.value = address.locality || ""
    if (this.hasCountyTarget) this.countyTarget.value = address.administrative_area_level_2 || ""
    if (this.hasStateTarget) this.stateTarget.value = address.administrative_area_level_1 || ""
    if (this.hasPostalCodeTarget) this.postalCodeTarget.value = address.postal_code || ""
    if (this.hasCountryTarget) {
      console.log(address.country);
      const countryName = address.country || ""
      const countrySelect = this.countryTarget
      const options = Array.from(countrySelect.options)
 
      const optionByName = options.find(option => 
        option.text.toLowerCase() === countryName.toLowerCase() ||
        option.text.toLowerCase().includes(countryName.toLowerCase())
      )
      
      if (optionByName) {
        countrySelect.value = optionByName.value
        const event = new Event('change', { bubbles: true })
        countrySelect.dispatchEvent(event)
      }
      
    }
  }

  setGeometry(geometry) {
    if (this.hasLongitudeTarget) this.longitudeTarget.value = geometry.location.lng().toString()
    if (this.hasLatitudeTarget) this.latitudeTarget.value = geometry.location.lat().toString()
  }

  get autocompleteOptions() {
    return {
      fields: ["address_components", "geometry"],
      componentRestrictions: {
        country: this.countryValue,
      },
    }
  }

  preventSubmit(event) {
    if (event.code === "Enter") {
      event.preventDefault()
    }
  }

  formatAddressComponents(addressComponents) {
    const data = {}

    addressComponents.forEach((component) => {
      const type = component.types[0]

      data[type] = component.long_name
    })

    return data
  }
}